/**
 * Forgot Password Web/Mobile - New Password Modal that allows a user to reset their Password
 *
 * @module views/components/ResetPasswordForm
 * @memberof -Common
 */
import './ResetPasswordForm.scss';

import React, { useCallback, useEffect } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import Button from '@ulta/core/components/Button/Button';
import InputField from '@ulta/core/components/InputField_New/InputField_New';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import TagGroupForInput from '@ulta/core/components/TagGroupForInput/TagGroupForInput';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { hasItems } from '@ulta/core/utils/array/array';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';

import { getIsButtonDisabled } from '@ulta/utils/Validations/Validations';

import * as utils from './ResetPasswordForm';


/**
* Represents a ResetPasswordForm component
*
* @method
* @param {ResetPasswordFormProps} props - React properties passed from composition
* @returns ResetPasswordForm
*/
export const ResetPasswordForm = function( props ){
  const {
    title,
    image,
    password,
    resetAction,
    cancelAction,
    passwordNoteLabel,
    loading,
    invokeMutation,
    resetToken,
    profileId,
    messages,
    componentKey,
    id: contentId
  } = props || {};

  const hasMessages = hasItems( messages );

  const [loader, showLoader, hideLoader] = useLoader( {} );

  const validations = hasItems( password?.validations ) ? password.validations : [];
  let passwordValidator = Yup.string().required();
  validations.forEach( validation => {
    passwordValidator = passwordValidator.matches( validation.regex ) ;
  } );
  const validationSchema = Yup.object( {
    'password': passwordValidator
  } );
  const initialValues = {
    password: ''
  };
  /*
  * Response handler composition
  * TODO: Make this a helper function
  */
  const responseHandler = useCallback( utils.composeResponseHandler( { loading }, { hideLoader } ), [loading] );
  useEffect( responseHandler, [responseHandler] );

  return (
    <Formik
      initialValues={ initialValues }
      validateOnMount
      enableReinitialize={ true }
      validationSchema={ validationSchema }
      onSubmit={ ( values, { resetForm } ) => {
        utils.handleSubmit( { values, resetAction, contentId, resetToken, profileId }, { invokeMutation, showLoader } );
        resetForm();
      } }
    >
      { ( fprops ) => (
        <div className='ResetPasswordForm'>
          { loader }
          { image &&
          <div className='ResetPasswordForm__Image'>
            <UltaAsset
              width={ 251 }
              { ...spreadProps( { ...image } ) }
            />
          </div>
          }
          <Form>
            { title &&
            <div className='ResetPasswordForm__Title'>
              <Text
                htmlTag='h1'
                textStyle='title-5'
                fontWeight='bold'
              >
                { title }
              </Text>
            </div>
            }
            { hasMessages &&
            messages?.map( ( data, index ) => (
              <div className='ResetPasswordForm__blockErrorMessage'
                key={ `${index}-${componentKey}` }
              >
                <BlockMessage
                  message={ data.message }
                  messageType={ data.icon }
                  backgroundColor={ data.backgroundColor }
                  borderColor={ data.borderColor }
                  textColor={ data.textColor }
                  dismissAction={ data.dismissAction }
                  action1={ data.action1 }
                  action1Style='link'
                  action2={ data.action2 }
                  ariaLiveType={ 'assertive' }
                  role={ 'status' }
                />
              </div>
            ) ) }
            { password &&
              <div className='ResetPasswordForm__Password'>
                <InputField
                  { ...spreadProps( { ...password } ) }
                  helpText=''
                  autoComplete='password'
                />
                <TagGroupForInput
                  tagGroupHeading={ password.helpText }
                  value={ fprops.values.password }
                  validations={ validations }
                />
              </div>
            }
            { passwordNoteLabel &&
              <div className='ResetPasswordForm__PasswordNoteLabel'>
                <Text
                  htmlTag='p'
                  textStyle='body-3'
                  color='neutral-500'
                >
                  { passwordNoteLabel }
                </Text>
              </div>
            }
            <div className='ResetPasswordForm__Submit'>
              <Button
                type='submit'
                primary
                disabled={ getIsButtonDisabled( { isValid: fprops.isValid, dirty: fprops.dirty } ) }
                fullWidth
                block={ true }
                label={ resetAction?.label }
              >
              </Button>
            </div>
            <div className='ResetPasswordForm__Cancel'>
              <Link_Huge
                likeButtonOutline
                url={ cancelAction?.url }
              >
                { cancelAction?.label }
              </Link_Huge>
            </div>
          </Form>
        </div>
      ) }
    </Formik>
  );
};

/**
* @function handleSubmit
* @description - handles the submit event on the confirm button
* @param {Object} methods - Passing methods as an argument for the mutation call
* @param {String} data - Passing data as an argument
*/

export const handleSubmit = ( data, methods ) => {
  const { values, resetAction, contentId, resetToken, profileId } = data || {};
  const { invokeMutation, showLoader } = methods || {};
  const { graphql, params, customHeaders } = resetAction || {};

  if( !graphql || !invokeMutation ){
    return;
  }

  showLoader();

  const moduleParams = { resetToken, profileId };
  moduleParams['newPassword'] = { 'name':'newPassword', 'value':values['password'] };
  Object.keys( values ).forEach( ( key ) => {
    moduleParams[key] = { name: key, value: values[key] };
  } );

  invokeMutation( {
    graphql,
    params,
    customHeaders,
    variables: {
      url: { path: !isServer() && global.location.pathname },
      contentId,
      moduleParams
    }
  } );
};

/**
* composeResponseHandler method to handle loader
* @param {object} data passing data.loading as an argument
* @param {object} methods passing methods.hideLoader as an argument
**/
export const composeResponseHandler = ( data, methods ) => () => {
  const { loading } = data || {};
  const { hideLoader } = methods || {};

  if( loading || !hideLoader ){
    return;
  }

  hideLoader();

};

/**
* Property type definitions
* @typedef ResetPasswordFormProps
* @type {object}
* @property {string} title - Sets the title
* @property {string} helpText - Sets the password label
* @property {string} passwordNoteLabel - Sets the password note
* @property {object} image - Sets the Ulta logo image
* @property {object} password - Sets password textbox
* @property {object} resetAction - Sets reset action button
* @property {object} cancelAction - Sets cancel action button
* @property {function} invokeMutation - Function to invoke the mutation
* @property {string} resetToken - set reset token
* @property {string} profileId - set profile id
*/
export const propTypes =  {
  /** Sets the title */
  title: PropTypes.string,
  /** Sets the password label */
  helpText: PropTypes.string,
  /** Sets the password note */
  passwordNoteLabel: PropTypes.string,
  /** Sets the Ulta logo image */
  image: PropTypes.object,
  /** Sets password textbox */
  password: PropTypes.object,
  /** Sets the reset action button */
  resetAction: PropTypes.object,
  /** Sets the cancel action button*/
  cancelAction: PropTypes.object,
  /** Function to invoke the mutation */
  invokeMutation: PropTypes.func,
  /** Sets the reset token */
  resetToken: PropTypes.string,
  /** Sets the profileId */
  profileId: PropTypes.string,
  /** Sets the messages */
  messages: PropTypes.array,
  /** Sets the componentKey */
  componentKey: PropTypes.string
};
ResetPasswordForm.propTypes = propTypes;
export default ResetPasswordForm;